import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const VALUE_FORMAT = 'YYYY-MM-DD';
export const LABEL_FORMAT = 'DD/MM/YYYY';

const I18N_PRESETS = 'components.date_range_preset_picker.presets';

/** A closed day range, both ends `YYYY-MM-DD`, inclusive. */
export interface DateRangeValue {
  from: string;
  to: string;
}

export interface DateRangePreset {
  key: string;
  /** An i18n key (translated by the picker) or an already translated text. */
  label: string;
  /** Interpolation values for `label` when it is an i18n key. */
  labelParams?: Record<string, string | number>;
  /**
   * The range for the given calendar day. `null` means the preset has no days
   * to offer today (e.g. "current week" up to yesterday on a Monday) and the
   * picker shows it disabled.
   */
  getRange: (today: Dayjs) => DateRangeValue | null;
}

export interface PresetOptions {
  /** `today` includes the current day; `yesterday` never goes past D-1. */
  endAt?: 'today' | 'yesterday';
  label?: string;
  key?: string;
}

export interface RangeConstraints {
  /** `pickedStart` is the endpoint already chosen, when there is one. */
  disabledDate?: (day: Dayjs, pickedStart?: Dayjs) => boolean;
  maxDays?: number;
}

/** Calendar days are handled as UTC midnights so no zone shifts them. */
export const parseDay = (value: string): Dayjs =>
  dayjs.utc(value, VALUE_FORMAT, true);

/** True when the days `a` and `b` (any order, both included) span over `maxDays`. */
export const exceedsMaxDays = (a: Dayjs, b: Dayjs, maxDays: number): boolean =>
  Math.abs(a.diff(b, 'day')) + 1 > maxDays;

/**
 * The calendar day of `now` in `timeZone` (the local one when omitted), as a
 * UTC-midnight `Dayjs` ready for day arithmetic.
 */
export const resolveToday = (now: Dayjs, timeZone?: string): Dayjs =>
  parseDay((timeZone ? now.tz(timeZone) : now).format(VALUE_FORMAT));

const toValue = (from: Dayjs, to: Dayjs): DateRangeValue => ({
  from: from.format(VALUE_FORMAT),
  to: to.format(VALUE_FORMAT),
});

/** The last day a preset may reach, given its `endAt` option. */
const endDay = (today: Dayjs, endAt: PresetOptions['endAt']): Dayjs =>
  endAt === 'yesterday' ? today.subtract(1, 'day') : today;

/** Caps `to` at `limit`; an empty (inverted) range becomes `null`. */
const capped = (
  from: Dayjs,
  to: Dayjs,
  limit: Dayjs
): DateRangeValue | null => {
  const end = to.isAfter(limit) ? limit : to;

  return from.isAfter(end) ? null : toValue(from, end);
};

/** Weeks run Monday to Sunday, whatever the dayjs locale says. */
const mondayOf = (day: Dayjs): Dayjs =>
  day.subtract((day.day() + 6) % 7, 'day');

const preset = (
  defaultKey: string,
  options: PresetOptions,
  getRange: DateRangePreset['getRange']
): DateRangePreset => ({
  key: options.key ?? defaultKey,
  label: options.label ?? `${I18N_PRESETS}.${defaultKey}`,
  getRange,
});

export const todayPreset = (options: PresetOptions = {}): DateRangePreset =>
  preset('today', options, (today) =>
    capped(today, today, endDay(today, options.endAt))
  );

export const yesterdayPreset = (options: PresetOptions = {}): DateRangePreset =>
  preset('yesterday', options, (today) => {
    const yesterday = today.subtract(1, 'day');

    return capped(yesterday, yesterday, endDay(today, options.endAt));
  });

export const currentWeekPreset = (
  options: PresetOptions = {}
): DateRangePreset =>
  preset('current_week', options, (today) => {
    const from = mondayOf(today);

    return capped(from, from.add(6, 'day'), endDay(today, options.endAt));
  });

export const previousWeekPreset = (
  options: PresetOptions = {}
): DateRangePreset =>
  preset('previous_week', options, (today) => {
    const from = mondayOf(today).subtract(7, 'day');

    return capped(from, from.add(6, 'day'), endDay(today, options.endAt));
  });

export const currentMonthPreset = (
  options: PresetOptions = {}
): DateRangePreset =>
  preset('current_month', options, (today) =>
    capped(
      today.startOf('month'),
      today.endOf('month').startOf('day'),
      endDay(today, options.endAt)
    )
  );

export const previousMonthPreset = (
  options: PresetOptions = {}
): DateRangePreset =>
  preset('previous_month', options, (today) => {
    const month = today.startOf('month').subtract(1, 'month');

    return capped(
      month,
      month.endOf('month').startOf('day'),
      endDay(today, options.endAt)
    );
  });

/** The `days` days ending today or yesterday (`endAt`), both included. */
export const lastNDaysPreset = (
  days: number,
  options: PresetOptions = {}
): DateRangePreset => ({
  key: options.key ?? `last_${days}_days`,
  label: options.label ?? `${I18N_PRESETS}.last_n_days`,
  labelParams: { days },
  getRange: (today) => {
    const end = endDay(today, options.endAt);

    return toValue(end.subtract(days - 1, 'day'), end);
  },
});

/** The preset's range, or `null` when it is empty or inverted. */
export const resolvePresetRange = (
  item: DateRangePreset,
  today: Dayjs
): DateRangeValue | null => {
  const range = item.getRange(today);

  return range && range.from <= range.to ? range : null;
};

/** First preset whose range equals `value` for the given day. */
export const findMatchingPreset = (
  value: DateRangeValue,
  presets: DateRangePreset[],
  today: Dayjs
): DateRangePreset | undefined =>
  presets.find((item) => {
    const range = resolvePresetRange(item, today);

    return range?.from === value.from && range?.to === value.to;
  });

export const formatRangeLabel = (value: DateRangeValue): string =>
  `${parseDay(value.from).format(LABEL_FORMAT)} – ${parseDay(value.to).format(
    LABEL_FORMAT
  )}`;

/**
 * True when a range fits the constraints: at most `maxDays` days and neither
 * endpoint disabled (the end is checked with the start as the picked day,
 * exactly like the calendar does while the user picks).
 */
export const isRangeAllowed = (
  range: DateRangeValue,
  { disabledDate, maxDays }: RangeConstraints
): boolean => {
  const from = parseDay(range.from);
  const to = parseDay(range.to);

  if (maxDays !== undefined && exceedsMaxDays(from, to, maxDays)) {
    return false;
  }
  if (!disabledDate) return true;

  return !disabledDate(from, undefined) && !disabledDate(to, from);
};
